import {
  Box,
  Flex,
  Spinner,
  Text,
  Table,
  Tr,
  Thead,
  Th,
  Tbody,
  Td
} from '@chakra-ui/react';
import {
  faEdit,
  faFile,
  faShieldCheck,
  faUpRightFromSquare,
  faWarning
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, ButtonText, OutlineCard, useToast } from '@morressier/alembic';
import { useQuery } from '@tanstack/react-query';
import {
  fetchSubmissionContext,
  fetchSubmissionContextArticleTypes
} from 'src/api/queries';
import { useUpdateJournalSettings } from 'src/api/mutations';
import { CheckTypeMap, ECheckType } from '@publishing-engine/domain/models';
import { useMemo, useState } from 'react';
import ChakraMultiSelect from 'src/components/MultiSelect';
import { Overlay } from 'src/components/Overlay';
import type { IIntegrityChecksConfig } from '@publishing-engine/domain/models';

const EVENT_OPTIONS = [
  { value: 'SubmissionCreated', label: 'Submission Created' },
  { value: 'RevisionSubmitted', label: 'Revision Submitted' },
  { value: 'SubmissionCloned', label: 'Submission Cloned' }
];

const CHECK_TYPE_OPTIONS = Object.entries(CheckTypeMap).map(
  ([value, label]) => ({
    value,
    label
  })
);

interface EventChecksState {
  SubmissionCreated: string[];
  RevisionSubmitted: string[];
  SubmissionCloned: string[];
}

const EMPTY_EVENT_CHECKS: EventChecksState = {
  SubmissionCreated: [],
  RevisionSubmitted: [],
  SubmissionCloned: []
};

const THRESHOLD_CONFIGURATION_URL =
  import.meta.env.VITE_RI_THRESHOLD_CONFIGURATION_URL;

interface Props {
  journalId: string;
}

export const IntegrityChecksSection: React.FC<Props> = ({ journalId }) => {
  const toast = useToast();
  const updateSettingMutation = useUpdateJournalSettings(journalId);
  const isSaving = updateSettingMutation.isPending;

  // Overlay state
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);
  const [selectedArticleTypeId, setSelectedArticleTypeId] =
    useState<string>('');
  const [selectedArticleTypeName, setSelectedArticleTypeName] =
    useState<string>('');
  const [eventChecks, setEventChecks] =
    useState<EventChecksState>(EMPTY_EVENT_CHECKS);

  const {
    data: journalArticleTypesResponse = { articleTypes: [] },
    isLoading: articleTypesLoading
  } = useQuery({
    queryKey: ['journalArticleTypes', journalId],
    queryFn: () => fetchSubmissionContextArticleTypes(journalId),
    enabled: !!journalId
  });

  const { data: journal, isLoading: journalLoading } = useQuery({
    queryKey: ['submission-context', journalId],
    queryFn: () => (journalId ? fetchSubmissionContext(journalId) : null),
    enabled: !!journalId
  });

  const isLoading = articleTypesLoading || journalLoading;
  const articleTypes = journalArticleTypesResponse.articleTypes;

  // Map articleTypeCategoryId → articleWorkflowId from the journal articleTypes
  const articleWorkflowIdMap = useMemo(() => {
    const map = new Map<string, string>();
    if (!journal?.articleTypes) return map;
    journal.articleTypes.forEach(at => {
      map.set(at.articleTypeCategoryId, at.articleWorkflowId);
    });
    return map;
  }, [journal]);

  // Extract existing config per article type from journal.config.integrityChecks
  const existingConfigMap = useMemo(() => {
    const map = new Map<string, EventChecksState>();
    const integrityChecks: IIntegrityChecksConfig[] =
      journal?.config?.integrityChecks ?? [];

    articleTypes.forEach(at => {
      const articleWorkflowId = articleWorkflowIdMap.get(at.id);
      const events: EventChecksState = {
        SubmissionCreated: [],
        RevisionSubmitted: [],
        SubmissionCloned: []
      };

      if (articleWorkflowId) {
        integrityChecks.forEach(ic => {
          const articleCheck = ic.articleChecks?.find(
            ac => ac.articleWorkflowId === articleWorkflowId
          );
          if (articleCheck && ic.event in events) {
            events[ic.event as keyof EventChecksState] =
              articleCheck.checks.map(c => c.checkType);
          }
        });
      }

      map.set(at.id, events);
    });

    return map;
  }, [journal, articleTypes, articleWorkflowIdMap]);

  const selectedWorkflowId = useMemo(
    () => articleWorkflowIdMap.get(selectedArticleTypeId) ?? null,
    [articleWorkflowIdMap, selectedArticleTypeId]
  );

  const openOverlay = (articleTypeId: string, articleTypeName: string) => {
    setSelectedArticleTypeId(articleTypeId);
    setSelectedArticleTypeName(articleTypeName);
    const existing = existingConfigMap.get(articleTypeId);
    setEventChecks(existing ? { ...existing } : { ...EMPTY_EVENT_CHECKS });
    setIsOverlayOpen(true);
  };

  const closeOverlay = () => {
    setIsOverlayOpen(false);
    setSelectedArticleTypeId('');
    setSelectedArticleTypeName('');
    setEventChecks({ ...EMPTY_EVENT_CHECKS });
  };

  const openThresholdConfiguration = () => {
    if (!THRESHOLD_CONFIGURATION_URL || !selectedWorkflowId) return;

    window.open(
      `${THRESHOLD_CONFIGURATION_URL}/${selectedWorkflowId}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  const handleCheckTypesChange = (eventType: string, value: string[]) => {
    setEventChecks(prev => ({
      ...prev,
      [eventType]: value
    }));
  };

  const handleSave = async () => {
    if (!selectedWorkflowId || !selectedArticleTypeId) {
      toast({
        variant: 'error',
        title: 'Article type or workflow not found',
        isClosable: true
      });
      return;
    }

    const existingAllChecks: IIntegrityChecksConfig[] =
      journal?.config?.integrityChecks ?? [];

    const newArticleChecks = EVENT_OPTIONS.map(event => ({
      event: event.value,
      articleChecks: [
        {
          articleWorkflowId: selectedWorkflowId,
          checks: (
            eventChecks[event.value as keyof EventChecksState] ?? []
          ).map(checkType => ({
            checkType: checkType as ECheckType,
            runType: event.value === 'RevisionSubmitted' ? 'manual' : 'auto'
          }))
        }
      ]
    }));

    const mergedChecks: IIntegrityChecksConfig[] = [];

    EVENT_OPTIONS.forEach(event => {
      const existingForEvent = existingAllChecks.find(
        ic => ic.event === event.value
      );
      const otherArticleChecks =
        existingForEvent?.articleChecks?.filter(
          ac => ac.articleWorkflowId !== selectedWorkflowId
        ) ?? [];

      const newForEvent = newArticleChecks.find(
        nac => nac.event === event.value
      );
      const newChecks = newForEvent?.articleChecks ?? [];

      mergedChecks.push({
        event: event.value,
        articleChecks: [...otherArticleChecks, ...newChecks]
      });
    });

    const res = await updateSettingMutation.mutateAsync({
      data: {},
      config: { integrityChecks: mergedChecks }
    } as any);

    if (res.isSuccess) {
      toast({
        variant: 'success',
        title: 'Integrity checks configuration saved successfully.',
        isClosable: true
      });
      closeOverlay();
      return;
    }

    toast({
      variant: 'error',
      title:
        typeof res?.message === 'string'
          ? res.message
          : 'Failed to save integrity checks configuration',
      isClosable: true
    });
  };

  if (isLoading) {
    return (
      <Flex justifyContent="center" py={8}>
        <Spinner />
      </Flex>
    );
  }

  return (
    <Flex flexDirection="column" gap={6}>
      {/* Header */}
      <Flex justifyContent="space-between" alignItems="center">
        <Text textStyle="headingSection">Integrity Checks</Text>
      </Flex>

      {articleTypes.length === 0 ? (
        <OutlineCard padding={8} backgroundColor="none">
          <Flex gap={8} flexDirection="column" alignItems="flex-start">
            <Flex
              width="100%"
              backgroundColor="neutralSecondaryFill"
              height="112px"
              borderRadius="base"
              justifyContent="center"
              alignItems="center"
            >
              <FontAwesomeIcon icon={faFile} />
            </Flex>
            <Box>
              <Text>No Article Types have been added to this Journal</Text>
              <Text>
                Add article types before configuring integrity checks.
              </Text>
            </Box>
          </Flex>
        </OutlineCard>
      ) : (
        <Box width="100%" overflowX="auto">
          <Table minWidth="640px" mx="auto">
            <Thead>
              <Tr
                data-testid="integrity-summary-header"
                sx={{
                  '& th': {
                    textTransform: 'none',
                    color: 'black',
                    background: 'white'
                  }
                }}
              >
                <Th p={{ base: 2, md: 4 }}>
                  <Text textStyle="headingItem" whiteSpace="nowrap">
                    Article Type
                  </Text>
                </Th>
                <Th p={{ base: 2, md: 4 }}>
                  <Text textStyle="headingItem" whiteSpace="nowrap">
                    Submission Created
                  </Text>
                </Th>
                <Th p={{ base: 2, md: 4 }}>
                  <Text textStyle="headingItem" whiteSpace="nowrap">
                    Revision Submitted
                  </Text>
                </Th>
                <Th p={{ base: 2, md: 4 }}>
                  <Text textStyle="headingItem" whiteSpace="nowrap">
                    Submission Cloned
                  </Text>
                </Th>
                <Th p={{ base: 2, md: 4 }}>
                  <Text textStyle="headingItem" whiteSpace="nowrap">
                    Status
                  </Text>
                </Th>
                <Th></Th>
              </Tr>
            </Thead>
            <Tbody>
              {articleTypes.map(at => {
                const config = existingConfigMap.get(at.id);
                const createdCount = config?.SubmissionCreated?.length ?? 0;
                const revisionCount = config?.RevisionSubmitted?.length ?? 0;
                const clonedCount = config?.SubmissionCloned?.length ?? 0;
                const totalChecks = createdCount + revisionCount + clonedCount;
                const isComplete =
                  createdCount > 0 && revisionCount > 0 && clonedCount > 0;

                return (
                  <Tr
                    key={at.id}
                    data-testid="integrity-summary-row"
                    background="white"
                    _hover={{ background: 'neutralSecondaryFill' }}
                  >
                    <Td isTruncated>
                      <Text isTruncated fontWeight="medium">
                        {at.name}
                      </Text>
                    </Td>
                    <Td>
                      <Text>{createdCount} check(s)</Text>
                    </Td>
                    <Td>
                      <Text>{revisionCount} check(s)</Text>
                    </Td>
                    <Td>
                      <Text>{clonedCount} check(s)</Text>
                    </Td>
                    <Td>
                      {isComplete ? (
                        <FontAwesomeIcon
                          icon={faShieldCheck}
                          style={{ color: 'green' }}
                        />
                      ) : (
                        <FontAwesomeIcon
                          icon={faWarning}
                          style={{ color: 'orange' }}
                        />
                      )}
                    </Td>
                    <Td>
                      <ButtonText
                        aria-label={`configure-integrity-${at.id}`}
                        variant="success"
                        onClick={() => openOverlay(at.id, at.name)}
                        leftIcon={<FontAwesomeIcon icon={faEdit} />}
                      >
                        {totalChecks > 0 ? 'Edit' : 'Configure'}
                      </ButtonText>
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </Box>
      )}

      {/* Configuration Overlay */}
      {isOverlayOpen && (
        <Overlay height="100%">
          <Flex
            flexDirection="column"
            backgroundColor="surfaceSecondaryFill"
            minH="100vh"
          >
            <Flex
              direction="column"
              gap={6}
              mx={{ base: 4, md: 8 }}
              mt={{ base: 4, md: 8 }}
              paddingBottom="220px"
            >
              {/* Header */}
              <Flex
                alignItems={{ base: 'flex-start', md: 'center' }}
                flexWrap="wrap"
                gap={3}
              >
                <Flex direction="column">
                  <Text textStyle="headingSection">
                    Integrity Checks Configuration
                  </Text>
                  <Text textStyle="textBody">
                    Configure integrity checks for{' '}
                    <Text as="span" fontWeight="semibold">
                      {selectedArticleTypeName}
                    </Text>
                  </Text>
                </Flex>
                <Flex direction="column" ml="auto" alignItems="flex-end">
                  <ButtonText
                    alignSelf="flex-start"
                    iconRight={<FontAwesomeIcon icon={faUpRightFromSquare} />}
                    onClick={openThresholdConfiguration}
                  >
                    Go to Threshold Configurations
                  </ButtonText>
                </Flex>
              </Flex>

              {!selectedWorkflowId ? (
                <Flex
                  p={4}
                  backgroundColor="neutralSecondaryFill"
                  borderRadius="base"
                  alignItems="center"
                  gap={2}
                >
                  <FontAwesomeIcon
                    icon={faWarning}
                    style={{ color: 'orange' }}
                  />
                  <Text>
                    No workflow is assigned to this article type. Please
                    configure the article type workflow first.
                  </Text>
                </Flex>
              ) : (
                EVENT_OPTIONS.map(event => {
                  const checks =
                    eventChecks[event.value as keyof EventChecksState] ?? [];
                  return (
                    <Flex key={event.value} direction="column" gap={2}>
                      <Text textStyle="headingItem">
                        {event.label}
                        <Text
                          as="span"
                          textStyle="textCaption"
                          color="textSecondary"
                          ml={2}
                        >
                          ({checks.length} check(s))
                        </Text>
                      </Text>
                      <Text textStyle="textCaption" color="textSecondary">
                        {event.value === 'RevisionSubmitted'
                          ? 'Checks will run manually for revisions'
                          : 'Checks will run automatically'}
                      </Text>
                      <ChakraMultiSelect
                        options={CHECK_TYPE_OPTIONS}
                        value={checks}
                        onChange={value =>
                          handleCheckTypesChange(event.value, value)
                        }
                        placeholder={`Select check types for ${event.label}`}
                        variant="interactive"
                        showSelectAllOption
                        selectAllLabel="Select All Checks"
                        showSearch
                      />
                    </Flex>
                  );
                })
              )}
            </Flex>

            {/* Sticky Footer */}
            <Flex
              padding={4}
              bottom={0}
              zIndex={1}
              justifyContent="flex-end"
              position="sticky"
              bgColor="surfacePrimaryFill"
              marginX="auto"
              width="100%"
            >
              <Flex gap={4}>
                <Button
                  variant="secondary"
                  onClick={closeOverlay}
                  isDisabled={isSaving}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleSave}
                  isLoading={isSaving}
                  isDisabled={!selectedWorkflowId}
                >
                  Save Configuration
                </Button>
              </Flex>
            </Flex>
          </Flex>
        </Overlay>
      )}
    </Flex>
  );
};
